/* MIS Industrial — product.js (product template only)
   Enhancements over the server-rendered baseline: variant sync via the
   Section Rendering API, AJAX add-to-cart, gallery, zoom dialog, copy
   buttons, sticky buy bar, recommendations fetch. The page purchases fine
   without any of this. */

function announce(message) {
  const region = document.getElementById('A11yStatus');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => { region.textContent = message; }, 50);
}

const root = document.querySelector('[data-product-root]');

/* ---------- Variant selection ---------- */
if (root) {
  const sectionId = root.dataset.sectionId;
  const productUrl = root.dataset.productUrl;
  const select = root.querySelector('[data-variant-select]');
  const optionGroups = root.querySelector('[data-option-groups]');
  const selectWrapper = root.querySelector('[data-variant-select-wrapper]');

  // Upgrade: show option pills, keep the native select as hidden source of truth.
  if (select && optionGroups) {
    optionGroups.hidden = false;
    selectWrapper.classList.add('visually-hidden');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    optionGroups.addEventListener('change', (e) => {
      const radio = e.target.closest('input[type="radio"]');
      if (!radio) return;
      // Combined listings: an option value may belong to another product.
      if (radio.dataset.productUrl) {
        window.location.href = radio.dataset.productUrl;
        return;
      }
      const chosen = Array.from(optionGroups.querySelectorAll('fieldset'))
        .map((fs) => fs.querySelector('input:checked'))
        .filter(Boolean)
        .map((input) => input.value);
      const title = chosen.join(' / ');
      const match = Array.from(select.options).find((opt) => (opt.dataset.title || opt.textContent.split(' — ')[0].trim()) === title);
      if (match) {
        select.value = match.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  async function refreshForVariant(variantId) {
    try {
      const url = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      document.querySelectorAll('[data-variant-refresh]').forEach((target) => {
        const key = target.dataset.variantRefresh;
        const source = doc.querySelector(`[data-variant-refresh="${key}"]`);
        if (source) {
          target.replaceChildren(...source.childNodes);
          if (key === 'gallery-main') {
            if (source.dataset.mediaId) target.dataset.mediaId = source.dataset.mediaId;
            else target.removeAttribute('data-media-id');
          }
        }
      });
      const params = new URLSearchParams(window.location.search);
      params.set('variant', variantId);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
      syncActiveThumb();
      bindAddToCart();
    } catch (e) {
      /* Server-rendered fallback still works; swallow network errors. */
    }
  }

  if (select) {
    select.addEventListener('change', () => refreshForVariant(select.value));
  }

  /* ---------- Gallery ---------- */
  const gallery = root.querySelector('[data-gallery]');
  function syncActiveThumb() {
    if (!gallery) return;
    const main = gallery.querySelector('[data-gallery-main]');
    const activeId = main ? main.dataset.mediaId : null;
    gallery.querySelectorAll('[data-thumb]').forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === activeId;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const activeThumb = gallery.querySelector(`[data-thumb][data-media-id="${activeId}"]`);
    const zoomLink = gallery.querySelector('[data-zoom-link]');
    if (zoomLink) {
      const mainImage = main && main.querySelector('img');
      const canZoom = activeThumb
        ? activeThumb.dataset.mediaType === 'image'
        : Boolean(mainImage && zoomLink.getAttribute('href'));
      zoomLink.hidden = !canZoom;
      if (!canZoom) zoomLink.removeAttribute('href');
      else if (activeThumb) zoomLink.href = activeThumb.href;
    }
  }
  if (gallery) {
    gallery.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-thumb]');
      if (!thumb) return;
      const template = gallery.querySelector(`[data-media-template="${thumb.dataset.mediaId}"]`);
      if (!template) return;
      e.preventDefault();
      const main = gallery.querySelector('[data-gallery-main]');
      main.replaceChildren(template.content.cloneNode(true));
      main.dataset.mediaId = thumb.dataset.mediaId;
      syncActiveThumb();
    });

    const dialog = gallery.querySelector('[data-zoom-dialog]');
    if (dialog && typeof dialog.showModal === 'function') {
      gallery.addEventListener('click', (e) => {
        const link = e.target.closest('[data-zoom-link]');
        if (!link || link.hidden || !link.href) return;
        e.preventDefault();
        const stage = dialog.querySelector('[data-zoom-stage]');
        const mainImg = gallery.querySelector('.gallery__main img');
        const img = document.createElement('img');
        img.src = link.href;
        img.alt = mainImg ? mainImg.alt : '';
        img.width = 2048;
        img.height = 2048;
        stage.replaceChildren(img);
        dialog.showModal();
      });
      dialog.querySelector('[data-zoom-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    }
  }
  syncActiveThumb();

  /* ---------- AJAX add to cart ---------- */
  function bindAddToCart() {
    document.querySelectorAll('form[data-product-form]').forEach((form) => {
      if (form.dataset.ajaxBound) return;
      form.dataset.ajaxBound = 'true';
      form.addEventListener('submit', async (e) => {
        const button = form.querySelector('[data-add-button]');
        if (!button || button.disabled) return;
        e.preventDefault();
        const errorBox = form.querySelector('[data-form-error]');
        const confirmation = form.querySelector('[data-add-confirmation]');
        if (errorBox) errorBox.hidden = true;
        button.disabled = true;
        try {
          const res = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          const data = await res.json();
          if (!res.ok) {
            if (errorBox) {
              errorBox.textContent = data.description || data.message || 'Error';
              errorBox.hidden = false;
            }
            return;
          }
          const cart = await (await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart.js`)).json();
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { item_count: cart.item_count } }));
          if (confirmation) confirmation.hidden = false;
          announce(confirmation ? confirmation.textContent.trim() : 'Added to cart');
        } catch (err) {
          // Network failure: fall back to the native full-page submit.
          form.removeAttribute('data-ajax-bound');
          form.submit();
        } finally {
          button.disabled = false;
        }
      });
    });
  }
  bindAddToCart();

  /* ---------- Copy identifier buttons ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-button]');
    if (!btn || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(btn.dataset.copyValue);
      announce(document.documentElement.lang.startsWith('en') ? 'Copied' : 'Copied');
    } catch (err) { /* clipboard unavailable */ }
  });

  /* ---------- Sticky buy bar ---------- */
  const stickyBar = document.querySelector('[data-sticky-atc]');
  const buyBlock = root.querySelector('[data-variant-refresh="buy"]');
  if (stickyBar && buyBlock && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        });
      },
      { rootMargin: '0px 0px -60px 0px' }
    );
    observer.observe(buyBlock);
  }
}

/* ---------- Product recommendations (real data or nothing) ---------- */
document.querySelectorAll('[data-recommendations]').forEach(async (container) => {
  const url = container.dataset.recommendationsUrl;
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) return;
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-recommendations]');
    if (fresh && fresh.innerHTML.trim().length > 0) {
      container.innerHTML = fresh.innerHTML;
    }
  } catch (e) { /* leave empty — never render placeholders */ }
});
